import { LogisticOperatorApi } from '@/pages/Business/pages/Commerce/services/logistic-operators.service';
import { useLogisticOperatorsOptions } from '@/pages/Business/pages/Commerce/hooks';
import { GlobalButtonVariant } from '@/components/buttons/GlobalButton';
import { DeleteForever as TrashIcon } from '@mui/icons-material';
import { Option } from '@/components/inputs/AutocompleteInput';
import { useEffect, useMemo, useState } from 'react';
import { driverStatusOptions } from '../config';
import { DriversFilter } from '../models';
import { useDebounce } from '@/hooks';
import {
  GlobalButton,
  SearchInput,
  SelectInput,
  AutocompleteInput,
} from '@/components';

interface SearchBarProps {
  onSearch: (filters: DriversFilter) => void;
  initialFilter: DriversFilter;
  currentFilter: DriversFilter;
}

const MIN_NAME_SEARCH_LENGTH = 3;

const getOptionId = (data: unknown): string | undefined => {
  if (typeof data === 'object' && data !== null && 'id' in data) {
    const id = (data as { id: unknown }).id;
    return typeof id === 'string' && id !== '' ? id : undefined;
  }
  return undefined;
};

const SearchBar = ({
  onSearch,
  initialFilter,
  currentFilter,
}: SearchBarProps) => {
  const [search, setSearch] = useState(currentFilter.search ?? '');
  const [email, setEmail] = useState(currentFilter.email ?? '');
  const [phone, setPhone] = useState(currentFilter.phone ?? '');
  const [status, setStatus] = useState(currentFilter.status ?? '');
  const [logisticOperatorInput, setLogisticOperatorInput] = useState('');
  const [selectedLogisticOperator, setSelectedLogisticOperator] =
    useState<Option | null>(() =>
      currentFilter.currentOperatorId
        ? {
            label: 'Operador seleccionado',
            data: { id: currentFilter.currentOperatorId },
          }
        : null
    );

  const debouncedSearch = useDebounce(search, 350);
  const debouncedEmail = useDebounce(email, 350);
  const debouncedPhone = useDebounce(phone, 350);
  const operatorId = getOptionId(selectedLogisticOperator?.data);

  const { operatorOptions: commerceOperatorOptions } =
    useLogisticOperatorsOptions({
      search: logisticOperatorInput,
      minLength: 3,
    });

  const operatorOptions = useMemo(() => {
    return commerceOperatorOptions.map((option) => ({
      ...option,
      label:
        (option.data as LogisticOperatorApi | undefined)?.name ||
        option.label.split(' - ')[0],
    }));
  }, [commerceOperatorOptions]);

  useEffect(() => {
    const trimmedSearch = debouncedSearch.trim();
    onSearch({
      search:
        trimmedSearch.length >= MIN_NAME_SEARCH_LENGTH
          ? trimmedSearch
          : undefined,
      email: debouncedEmail.trim() || undefined,
      phone: debouncedPhone.trim() || undefined,
      status: status || undefined,
      currentOperatorId: operatorId,
    });
  }, [
    debouncedSearch,
    debouncedEmail,
    debouncedPhone,
    status,
    operatorId,
    onSearch,
  ]);

  const resetFilter = () => {
    setSearch('');
    setEmail('');
    setPhone('');
    setStatus('');
    setLogisticOperatorInput('');
    setSelectedLogisticOperator(null);
    onSearch(initialFilter);
  };

  return (
    <div className='w-full flex flex-col justify-center gap-5 p-5 bg-white rounded-sm shadow-xl'>
      <h3 className='text-lg font-semibold text-gray-800'>
        Filtros de búsqueda
      </h3>
      <div className='grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-4'>
        <SearchInput
          name='driver-search'
          placeholder='Nombre y apellido'
          value={search}
          onChange={setSearch}
        />
        <SearchInput
          name='driver-email-search'
          placeholder='Email'
          value={email}
          onChange={setEmail}
        />
        <SearchInput
          name='driver-phone-search'
          placeholder='Teléfono'
          value={phone}
          onChange={setPhone}
        />
        <AutocompleteInput
          name='driver-logistic-operator-search'
          placeholder='Operador logístico'
          value={selectedLogisticOperator}
          onChange={setSelectedLogisticOperator}
          onInputChange={setLogisticOperatorInput}
          options={operatorOptions}
          required={false}
        />
        <SelectInput
          name='driver-status-search'
          placeholder='Estado'
          value={status}
          onChange={setStatus}
          options={driverStatusOptions}
        />
      </div>
      <div className='flex gap-3 justify-end'>
        <GlobalButton
          variant={GlobalButtonVariant.secondaryButton}
          action={resetFilter}
        >
          <TrashIcon color='primary' />
          Limpiar
        </GlobalButton>
      </div>
    </div>
  );
};

export default SearchBar;
